import { useEffect, useState } from 'preact/hooks'
import { X, Building2, Save } from 'lucide-preact'
import type { ClientCompanyUpdateDto, ClientCompanyViewDto } from '@/types/company'
import { clientCompanyApi } from '@/api/clientCompany'
import { CompanyEditForm } from './CompanyEditForm'
import { handleApiError } from '../../../utils/errorHandling'
import { useToast } from '../../../hooks/useToast'

interface CompanyEditModalProps {
  isOpen: boolean
  company: ClientCompanyViewDto
  onClose: () => void
  onSaved: (updated: ClientCompanyViewDto) => void
}

export function CompanyEditModal({ isOpen, company, onClose, onSaved }: CompanyEditModalProps) {
  const [editing, setEditing] = useState<ClientCompanyUpdateDto | null>(null)
  const [loading, setLoading] = useState(false)
  const { showError, showSuccess } = useToast()

  useEffect(() => {
    if (isOpen) {
      setEditing({
        ETag: company.ETag,
        AuthOrganizationId: company.AuthOrganizationId,
        Name: company.Name,
        Slug: company.Slug,
        CompanyId: company.CompanyId,
        CompanyUrl: company.CompanyUrl,
        CompanyType: company.CompanyType,
        Address: company.Address,
        Status: company.Status,
        TrackingId: company.TrackingId,
        TrackingEnabled: company.TrackingEnabled
      })
    }
  }, [isOpen, company])

  if (!isOpen) return null

  // Derive a fallback form state from the incoming company while local state initializes
  const formCompany: ClientCompanyUpdateDto = editing || {
    ETag: company.ETag,
    AuthOrganizationId: company.AuthOrganizationId,
    Name: company.Name,
    Slug: company.Slug,
    CompanyId: company.CompanyId,
    CompanyUrl: company.CompanyUrl,
    CompanyType: company.CompanyType,
    Address: company.Address,
    Status: company.Status,
    TrackingId: company.TrackingId,
    TrackingEnabled: company.TrackingEnabled
  }

  const handleSave = async (payload?: ClientCompanyUpdateDto) => {
    setLoading(true)
    try {
      const body = payload || formCompany
      const res = await clientCompanyApi.update(company.Id, body)
      if (res.error) {
        // Global handler shows toasts for 422; handleApiError handles others
        handleApiError(res, showError, () => {}, 'Failed to save company')
      } else if (res.data) {
        // Keep workspaces from original in case API doesn't include them
        const updated: ClientCompanyViewDto = { ...res.data, Workspaces: company.Workspaces }
        onSaved(updated)
        showSuccess('Success', 'Company information updated successfully')
        onClose()
      }
    } catch (err) {
      handleApiError(err, showError, () => {}, 'Failed to save company')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Edit Company</h2>
              <p className="text-sm text-slate-600 dark:text-slate-300">Update company information</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <CompanyEditForm
            company={formCompany}
            onSave={(vals) => handleSave(vals)}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
          <button form="company-edit-form" type="submit" disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
            <Save className="w-4 h-4" />
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
